import React, { useEffect, useMemo, useState } from "react"

import { css } from "@emotion/react"
import { Select } from "antd"
import awesomeDebouncePromise from "awesome-debounce-promise"

import type {
    ISelectRawOption,
    ISelectRenderedOption,
} from "@front-shared/types/objects.types"

import { UtilHelper } from "../helpers/util.helper"
import { FloatingLabelElement } from "./floatingLabel.element"

import searchIcon from "@front-shared/assets/images/icons/arrow-down-dark2.svg"

export const TagsSelectElement: React.FunctionComponent<
    ITagsSelectElementProps
> = props => {
    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false)
    const [searchedOptions, setSearchedOptions] = useState<ISelectRawOption[]>(
        [],
    )

    const debouncedOptionsFetcher = useMemo(() => {
        if (!props.optionsFetcher) {
            return null
        }

        return awesomeDebouncePromise(
            props.optionsFetcher,
            props.optionsFetcherDebounceMilliseconds || 500,
        )
    }, [props.optionsFetcher])

    useEffect(() => {
        if (props.searchOnFocus) {
            void onSearch()
        }

        if (props.defaultValue) {
            setSelectedItems(props.defaultValue)
        }
    }, [])

    useEffect(() => {
        if (props.externalValue) {
            setSelectedItems(props.externalValue)
        }
    }, [props.externalValue])

    const onChange = (selected: string[]) => {
        const filtered = !props.tagValidatorRegex
            ? selected
            : selected.filter(item => props.tagValidatorRegex?.test(item))

        setSelectedItems(filtered)

        props.onSelected(filtered)
    }

    const onSearch = async (search = "") => {
        if (!debouncedOptionsFetcher) {
            return
        }

        const searchedItems = await debouncedOptionsFetcher(search)

        let searchedOptions: ISelectRawOption[] = searchedItems.length
            ? UtilHelper.isObjectOfType<ISelectRawOption>(
                  searchedItems[0],
                  "label",
              )
                ? (searchedItems as ISelectRawOption[])
                : (searchedItems as ISelectRenderedOption[]).map(item => ({
                      key: item.key,
                      label: item.displayValue,
                      value: item.value,
                  }))
            : []

        // Make sure key is present
        searchedOptions = searchedOptions.map(item => ({
            ...item,

            key: item.key || `${UtilHelper.getRandomInt()}_${item.value}`,
        }))

        setSearchedOptions(searchedOptions)
    }

    const availableOptions = useMemo(() => {
        const initialOptions = [...searchedOptions, ...(props.options || [])]
        let resultOptions = initialOptions

        // remove duplicates when options and optionFetcher are use at same time
        if (!props.keepDuplicatedValueOptions) {
            resultOptions = resultOptions.filter((value, index, self) => {
                return (
                    index ===
                    self.findIndex(inner => inner.value === value.value)
                )
            })
        }

        // make sure that key exists in options
        // this is to keep previous code logic unchanged - most likely it fails without key under certain conditions
        // additionally of one of duplicated options was disabled -> keep disabled property for final option
        resultOptions = resultOptions.map(item => ({
            ...item,

            key:
                item.key ||
                `${UtilHelper.generateUniqueVarchar()}_${item.value}`,

            disabled: !!initialOptions.find(
                innerItem =>
                    innerItem.value === item.value && innerItem.disabled,
            ),
        }))

        return resultOptions
    }, [searchedOptions, props.options])

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            {props.label && (
                <FloatingLabelElement
                    label={props.label}
                    isFloated={true}
                    withRequiredStar={props.withRequiredStar}
                    tooltip={props.tooltip}
                />
            )}

            <Select
                size={props.size || "large"}
                placeholder={props.placeholder}
                className="w-full"
                css={[styles.select]}
                disabled={props.disabled}
                onChange={onChange}
                onFocus={() => {
                    if (props.searchOnFocus) {
                        setIsSelectOpen(true)
                    }
                }}
                onBlur={() => {
                    if (props.searchOnFocus) {
                        setIsSelectOpen(false)
                    }
                }}
                open={props.searchOnFocus ? isSelectOpen : undefined}
                dropdownMatchSelectWidth={false}
                allowClear={false}
                value={selectedItems}
                mode={!props.allowUserDefinedOptions ? "multiple" : "tags"}
                options={availableOptions}
                suffixIcon={
                    <img src={searchIcon} alt="Select" title="Select" />
                }
                maxTagCount={props.maxTagCount || 20}
                notFoundContent={null}
                onSearch={search => {
                    void onSearch(search)
                }}
                defaultValue={props.defaultValue}
                filterOption={
                    !props.allowUserDefinedOptions
                        ? (
                              inputValue?: string,
                              option?: ISelectRawOption,
                          ): boolean => {
                              return inputValue
                                  ? !!option?.label
                                        ?.toString()
                                        .trim()
                                        .toLowerCase()
                                        .includes(
                                            inputValue.trim().toLowerCase(),
                                        )
                                  : true
                          }
                        : undefined
                }
                dropdownRender={props.dropdownRender}
            />
        </div>
    )
}

const styles = {
    select: css({
        borderRadius: 6,

        // Prevent multi-row wrapping when maxTagCount overflow occurs
        ".ant-select-selector": {
            flexWrap: "nowrap",
            overflow: "hidden",
        },
        ".ant-select-selection-overflow": {
            flexWrap: "nowrap",
            overflow: "hidden",
        },
    }),
}

interface ITagsSelectElementProps {
    name: string
    className?: string
    label?: string
    tooltip?: string
    externalValue?: string[]
    onSelected: (items: string[]) => void
    placeholder?: string
    disabled?: boolean
    size?: "large" | "small"
    options?: ISelectRawOption[]
    allowUserDefinedOptions?: boolean
    keepDuplicatedValueOptions?: boolean
    optionsFetcher?: (
        search: string,
    ) => Promise<(ISelectRawOption | ISelectRenderedOption)[]>
    optionsFetcherDebounceMilliseconds?: number
    prefixIcon?: string
    maxTagCount?: number
    tagValidatorRegex?: RegExp
    withRequiredStar?: boolean
    searchOnFocus?: boolean
    defaultValue?: string[]
    dropdownRender?: (originNode: JSX.Element) => JSX.Element
}
